import { useRef, useState, useEffect, useCallback } from "react";
import { useEntranceAnimation } from "@/hooks/useEntranceAnimation";
import { newBooksData } from "@/mocks/newBooksData";
import { getBookPublicationBadge, getBookPublicationBadgeClass, getBookPublicationBadgeLabel } from "../../../../../shared/book-publication-status";

export default function NewBooksSection() {
  const { ref, isVisible } = useEntranceAnimation({ threshold: 0.1, sectionIndex: 2 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const books = newBooksData;
  const hasBooks = books.length > 0;

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);

    const cardWidth = el.firstElementChild
      ? (el.firstElementChild as HTMLElement).getBoundingClientRect().width +
        16
      : 280;
    const newIndex = Math.round(scrollLeft / cardWidth);
    setCurrentIndex(Math.min(newIndex, books.length - 1));
  }, [books.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    updateScrollState();
    return () => el.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  const scrollBy = useCallback(
    (direction: "left" | "right") => {
      const el = scrollRef.current;
      if (!el) return;
      const cardWidth = el.firstElementChild
        ? (el.firstElementChild as HTMLElement).getBoundingClientRect().width +
          16
        : 280;
      el.scrollBy({
        left: direction === "left" ? -cardWidth : cardWidth,
        behavior: "smooth",
      });
    },
    []
  );

  const scrollToIndex = useCallback((index: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.firstElementChild
      ? (el.firstElementChild as HTMLElement).getBoundingClientRect().width +
        16
      : 280;
    el.scrollTo({
      left: index * cardWidth,
      behavior: "smooth",
    });
  }, []);

  if (!hasBooks) {
    return (
      <section
        ref={ref}
        className="relative flex items-center justify-center bg-background-50"
        style={{ minHeight: "60vh" }}
      >
        <div className="page-container py-16 md:py-20 text-center">
          <div className="flex flex-col items-center justify-center py-16 md:py-20">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
              <i className="ri-book-line text-2xl"></i>
            </div>
            <p className="text-base md:text-lg text-foreground-500 font-medium">
              目前尚無可顯示的2026最新發行書籍。
            </p>
            <p className="mt-2 text-sm text-foreground-400">
              完整書目資訊可前往崧博出版書籍資料庫查詢。
            </p>
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="nofollow noopener"
              className="mt-6 px-6 py-3 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              查詢完整書目
              <i className="ri-external-link-line text-xs"></i>
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={ref}
      className="relative bg-background-50"
      style={{ minHeight: "80vh" }}
    >
      <div className="page-container py-16 md:py-20">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <div
            className={`transition-all duration-[600ms] ease-out ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0ms" }}
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-wide">
              2026 最新發行
            </h2>
          </div>
          <div
            className={`transition-all duration-[600ms] ease-out ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "100ms" }}
          >
            <p className="mt-4 md:mt-5 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              查看千華駐科技於 2026 年最新發行的系列作品與華文閱讀內容。
            </p>
          </div>
        </div>

        {/* Carousel */}
        <div
          className={`relative mt-10 md:mt-14 transition-all duration-[600ms] ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "200ms" }}
        >
          {/* Left Arrow */}
          <button
            onClick={() => scrollBy("left")}
            aria-label="向左滑動"
            disabled={!canScrollLeft}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-all cursor-pointer ${
              canScrollLeft
                ? "opacity-100"
                : "opacity-30 cursor-not-allowed"
            }`}
          >
            <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
          </button>

          {/* Right Arrow */}
          <button
            onClick={() => scrollBy("right")}
            aria-label="向右滑動"
            disabled={!canScrollRight}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-all cursor-pointer ${
              canScrollRight
                ? "opacity-100"
                : "opacity-30 cursor-not-allowed"
            }`}
          >
            <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
          </button>

          {/* Scroll Container */}
          <div
            ref={scrollRef}
            className="flex overflow-x-auto gap-4 px-2 md:px-10 pb-4 scroll-smooth scrollbar-hide"
            style={{
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {books.map((book, index) => (
              <div
                key={book.isbn}
                className="flex-shrink-0 w-[calc(50%-0.5rem)] sm:w-[calc(33.333%-0.75rem)] md:w-[calc(25%-0.75rem)] lg:w-[calc(20%-0.8rem)] scroll-snap-align-start"
                style={{ scrollSnapAlign: "start" }}
              >
                <div
                  className={`group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden transition-all duration-[500ms] ease-out ${
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-8"
                  }`}
                  style={{ transitionDelay: `${200 + index * 100}ms` }}
                >
                  {/* Cover */}
                  <div className="relative aspect-[3/4] flex items-center justify-center bg-background-100 p-4 overflow-hidden">
                    <img
                      src={book.cover}
                      alt={`《${book.title}》書封`}
                      title={`《${book.title}》`}
                      className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                    {(() => {
                      const badge = getBookPublicationBadge('publishDate' in book ? book.publishDate : '');
                      return badge ? (
                        <span className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-xs font-medium ${getBookPublicationBadgeClass(badge)}`}>
                          {getBookPublicationBadgeLabel(badge)}
                        </span>
                      ) : null;
                    })()}
                  </div>

                  {/* Book Info */}
                  <div className="p-4 md:p-5 flex flex-col gap-3">
                    {/* Title - full, no truncation */}
                    <h3 className="text-sm md:text-[15px] font-semibold text-foreground-950 leading-relaxed">
                      {book.title}
                    </h3>

                    {/* Author */}
                    <p className="text-xs md:text-[13px] text-foreground-600 leading-relaxed">
                      {book.author}
                    </p>

                    {/* ISBN - visible identifier */}
                    <p className="text-[11px] text-foreground-400 tracking-wide pt-1 border-t border-background-200/70">
                      ISBN {book.isbn}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots Indicator */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {books.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                aria-label={`跳至第 ${index + 1} 本書`}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentIndex
                    ? "bg-primary-500 w-5"
                    : "bg-background-300 hover:bg-background-400"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Bottom catalog link */}
        <div
          className={`mt-12 md:mt-16 text-center transition-all duration-[600ms] ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "700ms" }}
        >
          <p className="text-sm text-foreground-500 max-w-xl mx-auto leading-relaxed">
            完整書名、作者、系列、書系及相關出版資訊，以崧博書籍資料庫中的個別書籍頁為準。
          </p>
          <a
            href="https://catalog.sonbook.net/"
            target="_blank"
            rel="nofollow noopener"
            className="mt-5 inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            前往書籍資料庫
            <i className="ri-external-link-line text-xs"></i>
          </a>
        </div>
      </div>

      {/* Hide scrollbar globally for this section */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}
